<script setup>
import {defineProps, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import CloseIcon from "@/Svgs/CloseIcon.svg.vue";
import ArrowForm from "@/Svgs/Components/Dropdown/Arrow.svg.vue";
import { router } from '@inertiajs/vue3';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    isDark: {
        type: Boolean,
        default: false
    },
    clickPosition: {
        type: Object,
        default: {
            left: 0,
            width: 0
        }
    },
    hasTitle: {
        type: Boolean,
        default: true
    },
    refParentComponent: {
        type: Object,
        default: null
    }
})

const emit = defineEmits(['close'])

const refDropdownComponent = ref(null)
const arrowLeft = ref(0)
const randomID = ref((Math.random() + 1).toString(36).substring(7))

const updateArrowPosition = () => {
    const dropdown = document.querySelector('.dropdownComponent')
    if (dropdown) {
        const dropdownRect = dropdown.getBoundingClientRect()
        arrowLeft.value = props.clickPosition.left - dropdownRect.left + props.clickPosition.width / 2 - 11
    }
};

watch(() => props.clickPosition, (newVal) => {
    if (props.isOpen) {
        nextTick(updateArrowPosition)
    }
})

const closeDropdown = () => {
    emit('close')
}

const handleClickOutside = (event) => {
    if (refDropdownComponent.value && ! refDropdownComponent.value.contains(event.target) && props.isOpen && (props.refParentComponent === null || (! props.refParentComponent.contains(event.target)))) {
        closeDropdown()
    }
}

const handleRouteChange = () => {
    if (props.isOpen) {
        closeDropdown()
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    router.on('success', handleRouteChange)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
    <Transition :duration="550" name="dropdownComponent-transition">
        <div
            ref="refDropdownComponent"
            v-show="isOpen"
            :class="['dropdownComponent', { '-is-dark': isDark}, { '-is-visible': isOpen}]"
        >
            <ArrowForm
                class="dropdownComponent-arrowForm"
                :is-dark="isDark"
                :style="{ left: arrowLeft + 'px' }"
                :id="randomID"
            />
            <div
                v-if="hasTitle"
                class="dropdownComponent-title"
            >
                <slot name="title"></slot>
                <CloseIcon
                    class="-is-clickable"
                    @click="closeDropdown"
                    :is-dark="isDark"
                />
            </div>
            <div class="dropdownComponent-body">
                <slot name="body"></slot>
            </div>
        </div>
    </Transition>
</template>

<style lang="scss" scoped>
@use 'include-media/dist/include-media' as *;
@use '~@/base/variables' as *;

.dropdownComponent {
    position: absolute;
    top: 80px;
    right: 20px;
    border: 1px solid $gray500;
    border-radius: 5px;
    width: 360px;
    background-color: white;
    z-index: 10;
    transform: translateY(0);
    opacity: 1;

    @include media($breakpoint-phone) {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        border-radius: 0;
        height: calc(100vh - 54px);
    }
    &-transition {
        &-enter {
            &-active {
                transition: all 0.3s ease-out;
            }
        }
        &-leave {
            &-active {
                transition: all 0.3s ease-out;
            }
        }
        &-enter-from,
        &-leave-to {
            transform: translateY(-10px);
            opacity: 0;
        }
    }
    &.-is-dark {
        background-color: $blue800;
        border-color: $blue800;

        .dropdownComponent {
            &-title {
                border-color: $blue700;
                color: white;
            }
            &-body {
                &::-webkit-scrollbar {
                    width: 10px;
                }
                &::-webkit-scrollbar-track {
                    background-color: $blue700;
                }
                &::-webkit-scrollbar {
                    background-color: white;
                }
                &::-webkit-scrollbar-thumb {
                    background-color: $blue800;
                }
            }
        }
    }
    &-arrowForm {
        position: absolute;
        top: -11px;
        right: 105px;

        @include media($breakpoint-phone) {
            display: none;
        }
        .-is-visible:not(.dropdownComponent-transition-enter-active):not(.dropdownComponent-transition-leave-active) & {
            transition: all 0.2s ease;
        }
    }
    &-title {
        border-bottom: 1px solid $gray500;
        font-size: 20px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 26px 30px;
    }
    &-body {
        max-height: 470px;
        overflow-y: auto;

        @include media($breakpoint-phone) {
            max-height: calc(100vh - 130px);
        }
        &::-webkit-scrollbar-track {
            background-color: white;
        }
        &::-webkit-scrollbar {
            width: 5px;
            background-color: white;
        }
        &::-webkit-scrollbar-thumb {
            background-color: $gray500;
        }
    }
}
</style>
